import React, { FC, useRef } from 'react';
import { TextField, Stack } from '@mediahuis/chameleon-react';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';

import './datePicker.css';

interface DatePickerFieldProps {
  fieldName: string;
  value?: string;
  error: boolean;
  onChange: (value: string) => void;
  onBlur?: () => void;
  label: string;
}

interface DatePickerProps {
  day: DatePickerFieldProps;
  month: DatePickerFieldProps;
  year: DatePickerFieldProps;
}

const DatePicker: FC<DatePickerProps> = (props) => {
  const birthdateDayElRef = useRef<HTMLInputElement>();
  const birthdateMonthElRef = useRef<HTMLInputElement>();
  const birthdateYearElRef = useRef<HTMLInputElement>();
  const { t } = useTranslation();
  const dateOninput = (field: 'D' | 'M' | 'Y', setFieldFn: (value: string) => void) => {
    const input = (
      field === 'D' ? birthdateDayElRef : field === 'M' ? birthdateMonthElRef : birthdateYearElRef
    ).current;
    if (!input) return;

    const focusNextDateInput = () => {
      if (input.value.length === input.maxLength) {
        const nextInput = field === 'D' ? birthdateMonthElRef : birthdateYearElRef;
        nextInput.current?.focus();
      }
    };
    // Ensure no other characters than numbers are entered
    const replacement = input.value.replace(/[^0-9.]/g, '').replace(/(\..*)\./g, '$1');

    if (input.value === replacement) {
      setFieldFn(input.value);
      // focus if maxlength reached
      focusNextDateInput();
    } else {
      input.value = replacement;
    }
  };
  const dateOnKeyDown = (field: 'D' | 'M' | 'Y', e: KeyboardEvent) => {
    const input = (
      field === 'D' ? birthdateDayElRef : field === 'M' ? birthdateMonthElRef : birthdateYearElRef
    ).current;
    if (!input) return;
    const caretPosition = input.selectionStart;
    const nextInput = (field === 'D' ? birthdateMonthElRef : birthdateYearElRef).current;
    const previousInput = (field === 'Y' ? birthdateMonthElRef : birthdateDayElRef).current;

    switch (e.code) {
      case 'ArrowLeft':
        if (field !== 'D' && previousInput && caretPosition === 0) {
          previousInput.focus();
          previousInput.selectionStart = previousInput.maxLength;
        }
        break;
      case 'ArrowRight':
        if (field !== 'Y' && nextInput && caretPosition === input.maxLength) {
          nextInput.focus();
          nextInput.selectionStart = 0;
        }
        break;
      case 'Backspace':
        if (field !== 'D' && previousInput && input.value.length === 0) {
          previousInput?.focus();
        }
        break;
    }
  };

  return (
    <Stack
      className="date-picker w-100 mt-0 g-7"
      flexDirection={{
        sm: 'row',
        md: 'row',
        lg: 'row',
        xl: 'row',
      }}
      flexWrap="nowrap">
      <TextField
        id={props.day.fieldName}
        name={props.day.fieldName}
        inputMode="numeric"
        pattern="[0-9]*"
        type="text"
        required
        minLength="1"
        maxLength="2"
        min="1"
        max="31"
        style={{ width: '24%' }}
        label="day"
        labelHidden
        placeholder={t('register.birthDate.placeholderDay')}
        message={props.day.label}
        value={props.day.value ?? ''}
        error={props.day.error}
        ref={birthdateDayElRef}
        onInput={() => dateOninput('D', (value: string) => props.day.onChange(value))}
        onBlur={props.day.onBlur}
        onKeyDown={(e: KeyboardEvent) => dateOnKeyDown('D', e)}
        className="date-picker__textfield"
        data-cy={props.day.fieldName}></TextField>
      <TextField
        id={props.month.fieldName}
        name={props.month.fieldName}
        inputMode="numeric"
        pattern="[0-9]*"
        type="text"
        required
        minLength="1"
        maxLength="2"
        min="1"
        max="12"
        style={{ width: '24%' }}
        label="month"
        labelHidden
        placeholder="MM"
        message={props.month.label}
        value={props.month.value ?? ''}
        error={props.month.error}
        ref={birthdateMonthElRef}
        onInput={() => dateOninput('M', (value: string) => props.month.onChange(value))}
        onBlur={props.month.onBlur}
        onKeyDown={(e: KeyboardEvent) => dateOnKeyDown('M', e)}
        className="date-picker__textfield"
        data-cy={props.month.fieldName}></TextField>
      <TextField
        id={props.year.fieldName}
        name={props.year.fieldName}
        inputMode="numeric"
        pattern="[0-9]*"
        type="text"
        required
        minLength="4"
        maxLength="4"
        min={(dayjs().year() - 200).toString()}
        max={dayjs().year().toString()}
        style={{ width: '52%' }}
        label="year"
        labelHidden
        placeholder={t('register.birthDate.placeholderYear')}
        message={props.year.label}
        value={props.year.value ?? ''}
        error={props.year.error}
        ref={birthdateYearElRef}
        onInput={() => dateOninput('Y', (value: string) => props.year.onChange(value))}
        onBlur={props.year.onBlur}
        onKeyDown={(e: KeyboardEvent) => dateOnKeyDown('Y', e)}
        className="date-picker__textfield"
        data-cy={props.year.fieldName}></TextField>
    </Stack>
  );
};

export default DatePicker;
